// Auditoría continua. Contabilidad → Libros y Auxiliares.
//
// Siete reglas que corren solas todas las mañanas y dejan un hallazgo cuando
// encuentran algo. Lo que hace un auditor externo en su primera semana, sólo
// que a diario.
//
// La decisión de diseño que sostiene la pantalla: un control que vuelve a
// gritar lo mismo cada día se ignora en una semana. Por eso cada hallazgo se
// puede marcar «revisado» (lo miré, sigue abierto) o «justificado» (es
// correcto, cállate para siempre), y por defecto sólo se listan los abiertos.
// Lo que la regla deja de encontrar no se borra: pasa a «resuelto» con su
// fecha, que es el rastro que pide cualquier auditoría.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle, ArrowRight, CheckCircle2, ChevronDown, ChevronRight,
  Loader2, PlayCircle, ShieldAlert, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { useTabsStore } from '@/stores/tabs.store';
import { rpc, usd } from './shared';

interface Hallazgo {
  id: string; regla: string; severidad: string; titulo: string; detalle: string | null;
  cantidad: number | null; importe: number | null; fecha: string | null; ruta: string | null;
  estado: string; activo: boolean; nota: string | null;
  primera_vez: string; ultima_vez: string; resuelto_at: string | null;
  marcado_por: string | null;
}
interface Regla {
  clave: string; titulo: string; que: string; porque: string;
  activa: boolean; severidad: string; params: Record<string, unknown>; abiertos: number;
}
interface Corrida {
  corrida_at: string; desde: string; hasta: string;
  nuevos: number; abiertos: number; resueltos: number; ms: number;
}
interface Resumen { nuevos: number; revisados: number; justificados: number; resueltos: number; alta: number }
interface Datos { filtro: string; hallazgos: Hallazgo[]; reglas: Regla[]; ultima_corrida: Corrida | null; resumen: Resumen }

const FILTROS = [
  { v: 'abiertos', l: 'Abiertos' },
  { v: 'nuevos', l: 'Sin revisar' },
  { v: 'justificados', l: 'Justificados' },
  { v: 'resueltos', l: 'Resueltos' },
  { v: 'todos', l: 'Todos' },
];

// Cuánto hace. Un «hace 3 horas» dice más que una marca de tiempo cuando lo que
// preguntas es si la auditoría de esta mañana ya corrió.
function hace(iso: string | null | undefined): string {
  if (!iso) return 'nunca';
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'hace un momento';
  if (m < 60) return `hace ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24);
  return `hace ${d} día${d === 1 ? '' : 's'}`;
}

const Severidad = ({ s }: { s: string }) => (
  <span className={`px-2 py-0.5 rounded-full text-xs whitespace-nowrap ${
    s === 'alta' ? 'bg-red-500/10 text-red-600'
      : s === 'media' ? 'bg-amber-500/10 text-amber-600'
      : 'bg-muted text-muted-foreground'}`}>
    {s === 'alta' ? 'Alta' : s === 'media' ? 'Media' : 'Baja'}
  </span>
);

const Estado = ({ h }: { h: Hallazgo }) => {
  if (!h.activo) return <span className="px-2 py-0.5 rounded-full text-xs bg-green-500/10 text-green-600">Resuelto</span>;
  if (h.estado === 'justificado') return <span className="px-2 py-0.5 rounded-full text-xs bg-blue-500/10 text-blue-600">Justificado</span>;
  if (h.estado === 'revisado') return <span className="px-2 py-0.5 rounded-full text-xs bg-muted text-muted-foreground">Revisado</span>;
  return <span className="px-2 py-0.5 rounded-full text-xs bg-primary/10 text-primary">Nuevo</span>;
};

export function AuditoriaContinua() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const openTab = useTabsStore((s) => s.openTab);
  const [filtro, setFiltro] = useState('abiertos');
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [verReglas, setVerReglas] = useState(false);

  const { data, isFetching } = useQuery<Datos>({
    queryKey: ['cont-auditoria', filtro],
    queryFn: () => rpc<Datos>('cont_auditoria', { _filtro: filtro }),
  });

  const refrescar = () => void qc.invalidateQueries({ queryKey: ['cont-auditoria'] });
  const hallazgos = data?.hallazgos ?? [];
  const reglas = data?.reglas ?? [];
  const resumen = data?.resumen;
  const corrida = data?.ultima_corrida ?? null;
  const tituloDe = useMemo(
    () => Object.fromEntries(reglas.map((r) => [r.clave, r.titulo])) as Record<string, string>,
    [reglas],
  );

  const ejecutar = useMutation({
    mutationFn: () => rpc<{ nuevos: number; abiertos: number; resueltos: number; ms: number }>('cont_auditoria_ejecutar', {}),
    onSuccess: (r) => {
      const partes = [
        r.nuevos > 0 ? `${r.nuevos} hallazgo(s) nuevo(s)` : 'sin hallazgos nuevos',
        r.resueltos > 0 ? `${r.resueltos} resuelto(s)` : null,
      ].filter(Boolean);
      if (r.nuevos > 0) toast.warning(partes.join(' · '));
      else toast.success(partes.join(' · '));
      setSel(new Set());
      refrescar();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const marcar = useMutation({
    mutationFn: (v: { ids: string[]; estado: string; nota?: string | null }) =>
      rpc<{ marcados: number }>('cont_auditoria_marcar', { _ids: v.ids, _estado: v.estado, _nota: v.nota ?? null }),
    onSuccess: (r) => { toast.success(`${r.marcados} hallazgo(s) actualizados`); setSel(new Set()); refrescar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const reglaSet = useMutation({
    mutationFn: (v: { clave: string; activa: boolean }) =>
      rpc<unknown>('cont_auditoria_regla_set', { _clave: v.clave, _activa: v.activa }),
    onSuccess: () => { toast.success('Regla actualizada'); refrescar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const alternar = (id: string) => setSel((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const todos = () => setSel((s) => (s.size === hallazgos.length ? new Set() : new Set(hallazgos.map((h) => h.id))));

  const marcarSel = async (estado: string) => {
    const ids = [...sel];
    if (!ids.length) return;
    let nota: string | null = null;
    if (estado === 'justificado') {
      const v = await dlg.pedir('Justificar hallazgos', [
        { key: 'nota', label: 'Por qué son correctos (queda registrado con tu usuario)', multilinea: true, minimo: 3 },
      ], { detalle: `Se justificarán ${ids.length} hallazgo(s) y dejarán de aparecer.`, aceptar: 'Justificar' });
      if (!v) return;
      nota = v.nota;
    }
    marcar.mutate({ ids, estado, nota });
  };

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-semibold">Auditoría Continua</h1>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">
            Última revisión {hace(corrida?.corrida_at)}
            {corrida && ` · ${corrida.ms} ms`}
          </span>
          <Button isLoading={ejecutar.isPending} onClick={() => ejecutar.mutate()}>
            <PlayCircle className="w-4 h-4 mr-1" /> Revisar ahora
          </Button>
        </div>
      </div>
      <p className="text-sm text-muted-foreground">
        Siete reglas corren solas cada mañana sobre los libros, los bancos y los comprobantes. Lo que encuentran
        queda aquí hasta que alguien lo mira. Marca <b>revisado</b> lo que ya miraste y <b>justificado</b> lo que
        es correcto y no quieres volver a ver.
      </p>

      {/* Resumen */}
      {resumen && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { l: 'Sin revisar', v: resumen.nuevos, c: 'text-primary' },
            { l: 'Severidad alta', v: resumen.alta, c: resumen.alta > 0 ? 'text-red-600' : '' },
            { l: 'Revisados', v: resumen.revisados, c: '' },
            { l: 'Justificados', v: resumen.justificados, c: 'text-blue-600' },
            { l: 'Resueltos', v: resumen.resueltos, c: 'text-green-600' },
          ].map((k) => (
            <div key={k.l} className="rounded-lg border p-3">
              <div className="text-xs text-muted-foreground">{k.l}</div>
              <div className={`text-xl font-semibold font-mono tabular-nums ${k.c}`}>{k.v}</div>
            </div>
          ))}
        </div>
      )}

      {/* Reglas: qué se está mirando y qué no */}
      <Card>
        <CardHeader className="pb-2">
          <button onClick={() => setVerReglas((v) => !v)} className="flex items-center gap-2 text-left">
            {verReglas ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            <CardTitle className="text-base">Reglas ({reglas.filter((r) => r.activa).length} de {reglas.length} activas)</CardTitle>
          </button>
        </CardHeader>
        {verReglas && (
          <CardContent className="space-y-2">
            <p className="text-xs text-muted-foreground">
              Cada empresa tiene su realidad: una farmacia factura los domingos y a un estudio contable eso le
              sería alarmante. Apaga la que no aplique para que el resto se siga leyendo.
            </p>
            {reglas.map((r) => (
              <div key={r.clave} className="flex flex-wrap items-start gap-3 rounded-lg border p-3">
                <button
                  onClick={() => reglaSet.mutate({ clave: r.clave, activa: !r.activa })}
                  disabled={reglaSet.isPending}
                  title={r.activa ? 'Apagar esta regla' : 'Encender esta regla'}
                  className={`mt-0.5 shrink-0 ${r.activa ? 'text-green-600' : 'text-muted-foreground'}`}>
                  {r.activa ? <ShieldCheck className="w-5 h-5" /> : <ShieldAlert className="w-5 h-5" />}
                </button>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`font-medium text-sm ${r.activa ? '' : 'text-muted-foreground line-through'}`}>{r.titulo}</span>
                    <Severidad s={r.severidad} />
                    {r.abiertos > 0 && (
                      <span className="text-xs font-mono tabular-nums text-muted-foreground">{r.abiertos} abierto(s)</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{r.que}</p>
                  <p className="text-xs text-muted-foreground/80 mt-0.5 italic">{r.porque}</p>
                </div>
              </div>
            ))}
          </CardContent>
        )}
      </Card>

      {/* Filtros y acciones en bloque */}
      <div className="flex flex-wrap items-center gap-2">
        {FILTROS.map((f) => (
          <button key={f.v} onClick={() => { setFiltro(f.v); setSel(new Set()); }}
            className={`px-3 py-1 rounded-full text-sm border ${
              filtro === f.v ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted'}`}>
            {f.l}
          </button>
        ))}
        {isFetching && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
        <div className="ml-auto flex items-center gap-2">
          {sel.size > 0 && (
            <>
              <span className="text-xs text-muted-foreground">{sel.size} seleccionado(s)</span>
              <Button variant="outline" size="sm" disabled={marcar.isPending} onClick={() => marcarSel('revisado')}>
                Marcar revisado
              </Button>
              <Button variant="outline" size="sm" disabled={marcar.isPending} onClick={() => marcarSel('justificado')}>
                Justificar
              </Button>
              <Button variant="outline" size="sm" disabled={marcar.isPending} onClick={() => marcarSel('nuevo')}>
                Reabrir
              </Button>
            </>
          )}
        </div>
      </div>

      {hallazgos.length === 0 && !isFetching && (
        <Card><CardContent className="py-12 text-center">
          <CheckCircle2 className="w-10 h-10 mx-auto text-green-600 mb-3" />
          <p className="font-medium">
            {filtro === 'abiertos' ? 'Ninguna regla encontró nada abierto.' : 'No hay hallazgos con este filtro.'}
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            {corrida
              ? `Última revisión ${hace(corrida.corrida_at)}, sobre el período ${corrida.desde} → ${corrida.hasta}.`
              : 'Todavía no se ha ejecutado ninguna revisión. Pulsa «Revisar ahora».'}
          </p>
        </CardContent></Card>
      )}

      {hallazgos.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" /> Hallazgos ({hallazgos.length})
            <button onClick={todos} className="ml-2 text-xs font-normal text-primary hover:underline">
              {sel.size === hallazgos.length ? 'Quitar selección' : 'Seleccionar todos'}
            </button>
          </CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {hallazgos.map((h) => (
              <div key={h.id}
                className={`flex flex-wrap items-start gap-3 rounded-lg border p-3 ${
                  !h.activo ? 'opacity-60'
                    : h.estado === 'justificado' ? ''
                    : h.severidad === 'alta' ? 'border-red-500/40 bg-red-500/5'
                    : 'border-amber-500/40 bg-amber-500/5'}`}>
                <input type="checkbox" className="mt-1 shrink-0" checked={sel.has(h.id)}
                  onChange={() => alternar(h.id)} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-sm">{h.titulo}</span>
                    <Severidad s={h.severidad} />
                    <Estado h={h} />
                  </div>
                  {h.detalle && <p className="text-xs text-muted-foreground mt-0.5">{h.detalle}</p>}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-xs text-muted-foreground">
                    <span>{tituloDe[h.regla] ?? h.regla}</span>
                    {h.importe != null && <span className="font-mono tabular-nums">{usd(h.importe)}</span>}
                    {h.cantidad != null && <span className="font-mono tabular-nums">{h.cantidad} documento(s)</span>}
                    <span>visto por primera vez {hace(h.primera_vez)}</span>
                    {h.resuelto_at && <span className="text-green-600">resuelto {hace(h.resuelto_at)}</span>}
                  </div>
                  {h.nota && (
                    <p className="mt-1 text-xs italic text-blue-600">
                      «{h.nota}»{h.marcado_por ? ` — ${h.marcado_por}` : ''}
                    </p>
                  )}
                </div>
                {h.ruta && (
                  <button onClick={() => openTab({ id: h.ruta!, title: (tituloDe[h.regla] ?? h.regla).slice(0, 28), path: h.ruta! })}
                    className="inline-flex items-center gap-1 text-sm text-primary hover:underline shrink-0">
                    Ir <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
